import { createSlice, Dispatch } from '@reduxjs/toolkit';

import { AyceCampaignsDto, AyceCampaignDto, getAyceCampaigns, getAyceCampaign } from 'apis/ayceCampaign';

import { showStatusMessage } from 'application/campaignForm/campaignCreate/visualFeedbackStore';
import { ReduxAction } from 'common/genericTypes';
import { getInitialRequestState, RequestState, RequestType } from 'common/RequestState';

type AyceState = {
    list: RequestType<AyceCampaignsDto[], AyceCampaignsDto[]>;
    ayceCampaign: RequestType<AyceCampaignDto>;
};

type SetPayloadActionType<S extends keyof AyceState> = {
    store: S;
    value: AyceState[S]['payload'];
};

const handleError = (dispatch: Dispatch, error: any) => {
    if (error && typeof error.message === 'string') {
        dispatch(showStatusMessage(error.message, 'error'));
    }
    if (error && Array.isArray(error.message) && error.message.every(e => typeof e === 'string')) {
        // nestjs class-validator errors
        dispatch(showStatusMessage(['Validation Error', ...error.message].join('\n'), 'error'));
    }
};

const initialState: AyceState = {
    list: getInitialRequestState([]),
    ayceCampaign: getInitialRequestState(),
} as const;

export const ayceSlice = createSlice({
    name: 'ayce',
    initialState,
    reducers: {
        startLoading(state, action: ReduxAction<keyof AyceState>) {
            state[action.payload].requestState = RequestState.InProgress;
        },
        loadCompleted(state, action: ReduxAction<keyof AyceState>) {
            state[action.payload].requestState = RequestState.Finished;
        },
        loadFailed(state, action: ReduxAction<keyof AyceState>) {
            state[action.payload].requestState = RequestState.Failed;
        },
        setPayload<S extends keyof AyceState>(state, action: ReduxAction<SetPayloadActionType<S>>) {
            state[action.payload.store].payload = action.payload.value;
        },
    },
});

export function fetchAyceCampaigns() {
    return async (dispatch: Dispatch): Promise<void> => {
        dispatch(ayceSlice.actions.startLoading('list'));
        try {
            const ayceResponse = await getAyceCampaigns();
            dispatch(
                ayceSlice.actions.setPayload({
                    store: 'list',
                    value: ayceResponse,
                }),
            );
            dispatch(ayceSlice.actions.loadCompleted('list'));
        } catch (error) {
            handleError(dispatch, error);
            dispatch(ayceSlice.actions.loadFailed('list'));
        }
    };
}

export function fetchAyceCampaign(id: string) {
    return async (dispatch: Dispatch): Promise<void> => {
        dispatch(ayceSlice.actions.startLoading('ayceCampaign'));
        try {
            const campaign = await getAyceCampaign(id);
            dispatch(
                ayceSlice.actions.setPayload({
                    store: 'ayceCampaign',
                    value: campaign,
                }),
            );
            dispatch(ayceSlice.actions.loadCompleted('ayceCampaign'));
        } catch (error) {
            handleError(dispatch, error);
            dispatch(ayceSlice.actions.loadFailed('ayceCampaign'));
        }
    };
}
